/**
 * CharcutnuvoAnalytics — Central analytics module
 * Pushes custom events to window.dataLayer for GTM/GA4.
 * Enable debug mode with ?debug_analytics=true in URL.
 *
 * Events tracked:
 *   store_finder_click — CTA clicks to store finder pages
 *   dtc_click          — CTA clicks to collections/products (buttons only)
 *   add_to_cart        — Add-to-cart form submissions
 *   email_signup       — Newsletter / email signup form submissions
 *   recipe_click       — Clicks to recipe blog articles
 */
(function () {
  'use strict';

  window.dataLayer = window.dataLayer || [];

  var params = new URLSearchParams(window.location.search);
  var DEBUG = params.get('debug_analytics') === 'true';
  var PAGE_TYPE = document.body.dataset.pageType || 'unknown';

  var CharcutnuvoAnalytics = {

    /**
     * Push a GA4-compatible event to the dataLayer.
     * Automatically merges page_type into every event.
     *
     * @param {string} eventName — GA4 event name
     * @param {Object} eventParams — Additional event parameters
     */
    track: function (eventName, eventParams) {
      var payload = Object.assign(
        { event: eventName, page_type: PAGE_TYPE },
        eventParams || {}
      );

      window.dataLayer.push(payload);

      if (DEBUG) {
        console.log(
          '%c[CN Analytics] ' + eventName,
          'color: #4CAF50; font-weight: bold;',
          payload
        );
      }
    },

    /**
     * Auto-initialize all delegated event bindings.
     */
    init: function () {
      this._bindStoreFinderClicks();
      this._bindDtcClicks();
      this._bindAddToCart();
      this._bindEmailSignup();
      this._bindRecipeClicks();

      if (DEBUG) {
        console.log(
          '%c[CN Analytics] Initialized — debug mode ON',
          'color: #FF9800; font-weight: bold;',
          { page_type: PAGE_TYPE }
        );
      }
    },

    /* ---------------------------------------------------------------
     * Event bindings — all use delegated listeners on document
     * ------------------------------------------------------------- */

    /**
     * store_finder_click — fires when a user clicks a link to the
     * store-finder or find-us page, or any element tagged with
     * data-cn-track="store-finder".
     */
    _bindStoreFinderClicks: function () {
      var self = this;
      document.addEventListener('click', function (e) {
        var link = e.target.closest(
          'a[href*="store-finder"], a[href*="find-us"], [data-cn-track="store-finder"]'
        );
        if (!link) return;

        self.track('store_finder_click', {
          cta_location: self._getCtaLocation(link)
        });
      });
    },

    /**
     * dtc_click — fires when a user clicks a CTA button that links
     * to a collection or product page. Only fires for elements with
     * the .button class OR data-cn-track="dtc" to avoid tracking
     * every navigation link.
     */
    _bindDtcClicks: function () {
      var self = this;
      document.addEventListener('click', function (e) {
        var link = e.target.closest(
          'a[href*="/collections"], a[href*="/products"], [data-cn-track="dtc"]'
        );
        if (!link) return;

        // Only fire for CTA buttons, not every collection/product link
        if (!link.classList.contains('button') && !link.dataset.cnTrack) return;

        self.track('dtc_click', {
          cta_location: self._getCtaLocation(link)
        });
      });
    },

    /**
     * add_to_cart — fires on form submission for add-to-cart forms.
     * Reads product_handle and collection_handle from body dataset
     * (set by Liquid in theme.liquid / product template).
     */
    _bindAddToCart: function () {
      var self = this;
      document.addEventListener('submit', function (e) {
        var form = e.target.closest('form[action*="/cart/add"]');
        if (!form) return;

        self.track('add_to_cart', {
          product_handle: document.body.dataset.productHandle || '',
          collection_handle: document.body.dataset.collectionHandle || ''
        });
      });
    },

    /**
     * email_signup — fires on form submission for newsletter /
     * email signup forms, including Klaviyo subscribe forms and
     * elements tagged with data-cn-track="email-signup".
     */
    _bindEmailSignup: function () {
      var self = this;
      document.addEventListener('submit', function (e) {
        var form = e.target.closest(
          'form[action*="subscribe"], form.newsletter-form, [data-cn-track="email-signup"]'
        );
        if (!form) return;

        self.track('email_signup', {
          form_location: self._getCtaLocation(form)
        });
      });
    },

    /**
     * recipe_click — fires when a user clicks a link to a recipe
     * blog article. Extracts the article handle from the URL path.
     */
    _bindRecipeClicks: function () {
      var self = this;
      document.addEventListener('click', function (e) {
        var link = e.target.closest('a[href*="/blogs/recipes"]');
        if (!link) return;

        var href = link.getAttribute('href') || '';
        var segments = href.split('/').filter(Boolean);
        // URL pattern: /blogs/recipes/{article-handle}
        // The article handle is the segment after "recipes"
        var recipesIndex = segments.indexOf('recipes');
        var articleHandle = (recipesIndex !== -1 && segments[recipesIndex + 1])
          ? segments[recipesIndex + 1].split('?')[0]
          : '';

        self.track('recipe_click', {
          article_handle: articleHandle
        });
      });
    },

    /* ---------------------------------------------------------------
     * Helpers
     * ------------------------------------------------------------- */

    /**
     * Find the nearest parent section to provide location context
     * for CTA tracking. Looks for elements with a class matching
     * "section-*" (Dawn theme convention).
     *
     * @param {HTMLElement} el — The element to locate
     * @returns {string} — Section class name or 'unknown'
     */
    _getCtaLocation: function (el) {
      var section = el.closest('[class*="section-"]');
      if (!section) return 'unknown';

      var classes = section.className.split(' ');
      for (var i = 0; i < classes.length; i++) {
        if (classes[i].indexOf('section-') === 0) {
          return classes[i];
        }
      }
      return 'unknown';
    }
  };

  // Auto-initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      CharcutnuvoAnalytics.init();
    });
  } else {
    CharcutnuvoAnalytics.init();
  }

  // Expose globally for external use
  window.CharcutnuvoAnalytics = CharcutnuvoAnalytics;
})();
